import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  MapPin, 
  Phone, 
  Mail, 
  ArrowLeft, 
  Shield, 
  Users, 
  Heart,
  Sparkles,
  CheckCircle
} from 'lucide-react';

export default function WelcomePage() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);  // ✨ default true (avoid stuck at opacity-0 if useEffect delays)
  const [showContent, setShowContent] = useState(true);

  useEffect(() => {
    // تأثير الظهور التدريجي
    setTimeout(() => setIsVisible(true), 100);
    setTimeout(() => setShowContent(true), 600);
  }, []);

  const handleContinue = () => {
    // حفظ أن المستخدم شاهد صفحة الترحيب
    sessionStorage.setItem('welcome_seen', 'true');
    // ✨ توجيه لـ /login إذا غير مسجل، أو /dashboard إذا مسجل
    const hasToken = !!localStorage.getItem('token');
    navigate(hasToken ? '/dashboard' : '/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center p-4" dir="rtl">
      {/* خلفية متحركة */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-600/5 rounded-full blur-3xl"></div>
      </div>

      {/* المحتوى الرئيسي */}
      <div className={`relative z-10 max-w-2xl w-full transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        
        {/* البطاقة الرئيسية */}
        <div className="bg-slate-800/40 backdrop-blur-xl rounded-3xl border border-emerald-500/20 shadow-2xl shadow-emerald-500/10 overflow-hidden">
          
          {/* الشريط العلوي */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 py-6 px-8 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10"></div>
            <div className="relative">
              <div className="inline-flex items-center justify-center w-24 h-24 mb-4">
                <img 
                  src="/nazario-logo.png" 
                  alt="فريق نازاريو" 
                  className="w-full h-full object-contain drop-shadow-lg"
                />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                حسـابـاتـي
              </h1>
              <p className="text-emerald-100 text-lg">نظام إدارة المحلات التجارية</p>
            </div>
          </div>

          {/* المحتوى */}
          <div className={`p-8 transition-all duration-700 delay-300 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
            
            {/* من نحن */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-2 mb-6">
                <Users className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">من نحن</span>
              </div>
              
              <p className="text-slate-300 text-lg leading-relaxed mb-6">
                تم تطوير هذا التطبيق بواسطة
              </p>
              
              <h2 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400 mb-6">
                فريق نازاريو
              </h2>
              
              <p className="text-slate-400 leading-relaxed">
                نظام متكامل لإدارة المحلات التجارية، صُمم خصيصاً لتلبية احتياجات السوق العراقي.
                يوفر لك كل ما تحتاجه لإدارة محلك بكفاءة واحترافية.
              </p>
            </div>

            {/* الموقع */}
            <div className="bg-slate-700/30 rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-center gap-3 text-slate-300 mb-4">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-400">الموقع</p>
                  <p className="text-white font-medium">قرية بارا تحتاني - العراق</p>
                </div>
              </div>
            </div>

            {/* معلومات التواصل */}
            <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-2xl p-6 border border-emerald-500/20 mb-6">
              <h3 className="text-center text-white font-bold mb-4 flex items-center justify-center gap-2">
                <Phone className="w-5 h-5 text-emerald-400" />
                للتواصل والاستفسار
              </h3>
              
              <div className="space-y-3">
                {/* رقم 1 */}
                <a 
                  href="https://wa.me/9647772719901" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-slate-800/50 hover:bg-slate-700/50 rounded-xl p-4 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                      <svg className="w-5 h-5 text-green-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs">واتساب / اتصال</p>
                      <p className="text-white font-mono text-lg" dir="ltr">07772719901</p>
                    </div>
                  </div>
                  <ArrowLeft className="w-5 h-5 text-slate-500 group-hover:text-emerald-400 transition-colors rotate-180" />
                </a>

                {/* رقم 2 */}
                <a 
                  href="https://wa.me/9647507940933" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-slate-800/50 hover:bg-slate-700/50 rounded-xl p-4 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                      <svg className="w-5 h-5 text-green-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs">واتساب / اتصال</p>
                      <p className="text-white font-mono text-lg" dir="ltr">07507940933</p>
                    </div>
                  </div>
                  <ArrowLeft className="w-5 h-5 text-slate-500 group-hover:text-emerald-400 transition-colors rotate-180" />
                </a>
              </div>

              {/* البريد الإلكتروني */}
              <div className="mt-4 pt-4 border-t border-slate-600/50">
                <a 
                  href="mailto:aomerali42hesabaty@gmail.com"
                  className="flex items-center justify-center gap-3 text-slate-400 hover:text-emerald-400 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span className="font-mono" dir="ltr">aomerali42hesabaty@gmail.com</span>
                </a>
              </div>
            </div>

            {/* مميزات سريعة */}
            <div className="grid grid-cols-3 gap-3 mb-8">
              <div className="bg-slate-700/30 rounded-xl p-3 text-center">
                <Shield className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                <p className="text-xs text-slate-400">آمن وموثوق</p>
              </div>
              <div className="bg-slate-700/30 rounded-xl p-3 text-center">
                <CheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                <p className="text-xs text-slate-400">سهل الاستخدام</p>
              </div>
              <div className="bg-slate-700/30 rounded-xl p-3 text-center">
                <Heart className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                <p className="text-xs text-slate-400">دعم مستمر</p>
              </div>
            </div>

            {/* زر المتابعة */}
            <Button 
              onClick={handleContinue}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white py-6 text-lg rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-300 hover:scale-[1.02]"
              data-testid="continue-to-app-btn"
            >
              <span>متابعة إلى التطبيق</span>
              <ArrowLeft className="w-5 h-5 mr-2 rotate-180" />
            </Button>

            {/* حقوق النشر */}
            <p className="text-center text-slate-500 text-sm mt-6">
              تم بواسطة فريق نازاريو في قرية بارا تحتاني-العراق
              <br />
              © 2026 جميع الحقوق محفوظة
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
